import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getCurrentUser, hasAccess } from '../api/user';
import { useAuthStore, useIsAuthenticated, useToken } from '../stores/authStore';
import type { AppRoute } from '../api/dtos/user.dto';

// Map paths to route keys for access control.
// Only CORE routes are listed here.  Plugin routes that are not in this
// map are allowed by default — plugins manage their own permissions via
// the permission registry (requiredPermissions on nav items).
const pathToRouteKey = (path: string): AppRoute | undefined => {
  const mapping: Record<string, AppRoute> = {
    '/chat': 'chat',
    '/': 'dashboard',
    '/dashboard': 'dashboard',
    '/settings': 'settings',
  };
  
  // Graph routes
  if (path.startsWith('/graph')) {
    return 'dashboard' as AppRoute; // Graph is accessible if dashboard is
  }
  // Settings sub-routes
  if (path.startsWith('/settings')) {
    return 'settings';
  }
  // Marketplace routes
  if (path.startsWith('/marketplace')) {
    return 'dashboard' as AppRoute;
  }
  // Workflows are a core feature — accessible to all authenticated users.
  // No allowedRoutes gate; returning undefined lets RequireAuth skip the
  // access check and render the page directly.
  if (path.startsWith('/workflows') || path.startsWith('/autonomous-workflows')) {
    return undefined;
  }
  // Report routes
  if (path.startsWith('/reports')) {
    return 'reports';
  }
  
  return mapping[path];
};

/** Where to send someone who may not open the page they asked for. */
const FALLBACK_ROUTES: Array<[string, AppRoute]> = [
  ['/', 'dashboard'],
  ['/chat', 'chat'],
  ['/settings', 'settings'],
];

export default function RequireAuth({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const token = useToken();
  const location = useLocation();
  const [isSyncing, setIsSyncing] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;

    const syncAuth = async () => {
      if (!token) {
        if (!cancelled) setIsSyncing(false);
        return;
      }

      try {
        const currentUser = await getCurrentUser();
        if (cancelled) return;

        const state = useAuthStore.getState();
        state.setAuth(
          token,
          state.userId ?? currentUser.username,
          currentUser.username,
          currentUser.role,
          currentUser.allowedRoutes || [],
        );
      } catch {
        // If refresh fails, fall back to the existing store state.
      } finally {
        if (!cancelled) setIsSyncing(false);
      }
    };

    void syncAuth();

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (isSyncing) {
    return null;
  }
  
  if (!isAuthenticated && !token) {
    return <Navigate to="/login" replace />;
  }
  
  // Check route access — only for known core routes.
  // Unknown routes (plugin pages) are allowed through; the plugin's own
  // permission requirements are enforced by the navigation registry.
  const routeKey = pathToRouteKey(location.pathname);
  if (routeKey && !hasAccess(routeKey)) {
    // Never send someone back to a page with the same gate (that looped
    // forever for users without the dashboard route).
    const fallback = FALLBACK_ROUTES.find(([, key]) => key !== routeKey && hasAccess(key));
    if (fallback) {
      return <Navigate to={fallback[0]} replace />;
    }
    return (
      <div role="alert" style={{ padding: 'var(--nc-space-6, 24px)', textAlign: 'center' }}>
        Your account doesn&apos;t have access to this page. Ask an admin to give you access.
      </div>
    );
  }
  
  return <>{children}</>;
}